/**
 * Shared chart drill-down hook (v1.4 UX fix — /salescoach/api/traces/list).
 *
 * One hook drives every TraceDrillDownModal. Callers pass the clicked-chart
 * `extras` (rating / latencyBand / complexity / bucket window) plus the
 * usual filters; the hook only fires when `enabled` is true so closing the
 * modal doesn't leave a subscription pinning the last-opened slice.
 *
 * Cache key shape mirrors the other hooks (`['traces', 'list',
 * ...extras, filtersQuestion(filters)]`) so toggling the global FilterBar
 * invalidates the modal's data in lockstep with the rest of the page.
 */

import { keepPreviousData } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import { getTracesList } from '@/services/api-client';
import type { TraceListExtras, TraceListResponse } from '@/services/api-types';

const STALE_TIME = 60_000;

export function useTracesList(
  filters: Filters,
  extras: TraceListExtras = {},
  enabled: boolean = true,
) {
  /* `sort`, `limit` and `offset` are all in this key and all of them blank
     it, because this endpoint returns a PAGE: re-sorting hands back the first
     50 rows in the new order, which is a different SET of traces, not the
     same ones rearranged (owner's ruling 3, 2026-08-20). That is what the
     reserved pagination row on UserDrilldownPage is for. A sort of an
     already-loaded table is the other case entirely and never reaches a key —
     see `filtersQuestion`. */
  return useFilteredQuery<TraceListResponse>({
    queryKey: [
    'v2',
    'traces',
    'list',
    extras.rating ?? null,
    extras.latencyBand ?? null,
    extras.complexity ?? null,
    extras.bucketFrom ?? null,
    extras.bucketTo ?? null,
    extras.dow ?? null,
    extras.hour ?? null,
    extras.excludeScheduled ?? null,
    // Phase 8 toolbar filters — every key the backend understands must be
    // in the cache key, otherwise toolbar input that flips a predicate
    // doesn't trigger a refetch (TanStack Query treats it as a cache hit
    // for the previous URL and never calls queryFn).
    extras.ratingMin ?? null,
    extras.ratingMax ?? null,
    extras.search ?? null,
    extras.errorsOnly ?? null,
    extras.hasFeedback ?? null,
    extras.sort ?? null,
    extras.limit ?? null,
    extras.offset ?? null,
    filtersQuestion(filters),
  ],
    queryFn: async () => (await getTracesList(filters, extras)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
    enabled,
  });
}
